<template>
  <main class="public-certificate">
    <ToolbarCore compact hide-guest-actions />

    <section class="certificate-hero">
      <div v-if="certificate" class="certificate-hero__content">
        <h1>Validacion de certificado</h1>
        <p>
          Este certificado fue encontrado en la base publica de verificacion.
        </p>
      </div>

      <div v-else class="certificate-hero__content">
        <v-chip small class="certificate-status certificate-status--error">
          <v-icon left size="15">mdi-alert-circle-outline</v-icon>
          No encontrado
        </v-chip>
        <h1>Certificado no disponible</h1>
        <p>
          El codigo ingresado no coincide con un certificado publico disponible.
        </p>
      </div>
    </section>

    <section v-if="certificate" class="certificate-layout">
      <aside
        :class="['certificate-summary', { 'certificate-summary--open': showMobileDetails }]"
        aria-label="Datos del certificado"
      >
        <div class="certificate-summary__header">
          <v-chip small class="certificate-status certificate-status--summary">
            <v-icon left size="15" color="green">mdi-check-decagram-outline</v-icon>
            Certificado verificado
          </v-chip>
          <v-btn
            class="certificate-summary__close"
            icon
            aria-label="Cerrar detalles"
            @click="showMobileDetails = false"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>

        <dl>
          <div>
            <dt>Codigo</dt>
            <dd>{{ certificate.code }}</dd>
          </div>
          <div>
            <dt>Curso</dt>
            <dd>{{ certificate.course_name }}</dd>
          </div>
          <div>
            <dt>Participante</dt>
            <dd>{{ certificate.student_name }}</dd>
          </div>
          <div>
            <dt>Estado</dt>
            <dd>{{ certificate.status_label }}</dd>
          </div>
          <div>
            <dt>Fecha de emision</dt>
            <dd>{{ certificate.issued_at }}</dd>
          </div>
          <div>
            <dt>Proveedor</dt>
            <dd>{{ certificate.provider }}</dd>
          </div>
        </dl>

        <div class="certificate-summary__actions">
          <a
            class="certificate-action certificate-action--primary"
            :href="certificate.file_url"
            target="_blank"
            rel="noopener noreferrer"
          >
            <v-icon size="18">mdi-open-in-new</v-icon>
            Abrir certificado
          </a>
          <a
            class="certificate-action certificate-action--download"
            :href="certificate.file_url"
            download
          >
            <v-icon size="18">mdi-download-outline</v-icon>
            Descargar
          </a>
          <button
            class="certificate-action certificate-action--disabled"
            type="button"
            disabled
          >
            <v-icon size="18">mdi-cube-outline</v-icon>
            Blockchain (No disponible)
          </button>
        </div>
      </aside>

      <article class="certificate-preview" aria-label="Vista previa del certificado">
        <div class="certificate-preview__header">
          <h2>Vista previa</h2>
          <div class="certificate-preview__header-actions">
            <span>{{ certificateFileTypeLabel }}</span>
            <v-btn
              class="certificate-details-trigger"
              icon
              small
              aria-label="Abrir detalles del certificado"
              @click="showMobileDetails = true"
            >
              <v-icon>mdi-menu</v-icon>
            </v-btn>
          </div>
        </div>

        <iframe
          v-if="isPdfCertificate"
          :src="certificatePreviewUrl"
          title="Certificado PDF"
          loading="lazy"
        />
        <img
          v-else
          :src="certificate.file_url"
          alt="Certificado"
        />
      </article>
    </section>

    <section v-else class="certificate-layout certificate-layout--empty">
      <section class="certificate-empty">
        <v-icon size="42">mdi-certificate-outline</v-icon>
        <h2>Revisa el codigo del certificado</h2>
        <p>
          Puedes intentar nuevamente usando una URL como
          <strong>/certificate/7F36A311A027</strong>.
        </p>
      </section>
    </section>
  </main>
</template>

<script>
import ToolbarCore from '../core/Toolbar.vue';

const CERTIFICATE_FILE_URL =
  'https://s3.amazonaws.com/assets.pilsala.com/courses/214/schedules/317/certificates/xertify/7F36A311A027.pdf';

const MOCK_CERTIFICATES = {
  '7F36A311A027': {
    code: '7F36A311A027',
    course_name: 'Curso Pilsa',
    student_name: 'Participante certificado',
    status: 'issued',
    status_label: 'Valido',
    provider: 'Xertify',
    issued_at: '2026',
    file_url: CERTIFICATE_FILE_URL,
  },
  '7F36A311A030': {
    code: '7F36A311A030',
    course_name: 'Curso Pilsa',
    student_name: 'Participante certificado',
    status: 'issued',
    status_label: 'Valido',
    provider: 'Xertify',
    issued_at: '2026',
    file_url: CERTIFICATE_FILE_URL,
  },
};

export default {
  name: 'PublicCertificate',
  components: {
    ToolbarCore,
  },
  data() {
    return {
      showMobileDetails: false,
    };
  },
  computed: {
    certificateCode() {
      return String(this.$route.params.code || '').trim().toUpperCase();
    },
    certificate() {
      return MOCK_CERTIFICATES[this.certificateCode] || null;
    },
    certificateExtension() {
      if (!this.certificate?.file_url) return '';

      const cleanUrl = this.certificate.file_url.split('?')[0];
      return cleanUrl.split('.').pop().toLowerCase();
    },
    isPdfCertificate() {
      return this.certificateExtension === 'pdf';
    },
    certificatePreviewUrl() {
      if (!this.isPdfCertificate) return this.certificate?.file_url || '';

      if (this.$vuetify.breakpoint.smAndDown) {
        return `https://docs.google.com/gview?embedded=true&url=${encodeURIComponent(this.certificate.file_url)}`;
      }

      return `${this.certificate.file_url}#page=1&view=FitH`;
    },
    certificateFileTypeLabel() {
      return this.isPdfCertificate ? 'PDF' : 'Imagen';
    },
  },
  watch: {
    showMobileDetails(isOpen) {
      document.documentElement.style.overflow = isOpen ? 'hidden' : '';
    },
  },
  beforeDestroy() {
    document.documentElement.style.overflow = '';
  },
};
</script>

<style scoped>
.public-certificate {
  min-height: 100vh;
  background: #f5f8fb;
  color: #101820;
  font-family: Poppins, Arial, sans-serif;
}

.certificate-hero {
  display: grid;
  gap: 22px;
  padding: 28px clamp(18px, 4vw, 64px) 34px;
  background: #fff;
  border-bottom: 1px solid #e8eef4;
}

.certificate-hero__content {
  max-width: 760px;
}

.certificate-status {
  margin-bottom: 14px;
  background: #e9f7fb !important;
  color: #0076ff !important;
  font: normal normal 700 12px/18px Poppins;
}

.certificate-status--error {
  background: #fff1f1 !important;
  color: #c33a3a !important;
}

.certificate-status--summary {
  margin-bottom: 0;
}

.certificate-hero h1 {
  margin: 0 0 8px;
  color: #101820;
  font: normal normal 700 clamp(24px, 4vw, 38px)/1.2 Poppins;
}

.certificate-hero p {
  margin: 0;
  color: #6b7785;
  font: normal normal 400 15px/24px Poppins;
}

.certificate-layout {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(280px, 360px);
  gap: 18px;
  padding: 22px clamp(18px, 4vw, 64px) 36px;
}

.certificate-summary,
.certificate-preview,
.certificate-empty {
  background: #fff;
  border: 1px solid #e8eef4;
  border-radius: 8px;
}

.certificate-summary {
  grid-column: 2;
  grid-row: 1;
  align-self: start;
  padding: 20px;
}

.certificate-summary__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.certificate-summary__close,
.certificate-details-trigger {
  display: none;
}

.certificate-summary h2,
.certificate-preview h2,
.certificate-empty h2 {
  margin: 0;
  color: #101820;
  font: normal normal 700 18px/26px Poppins;
}

.certificate-summary dl {
  display: grid;
  gap: 14px;
  margin: 18px 0 0;
}

.certificate-summary dl div {
  display: grid;
  gap: 3px;
  padding-bottom: 12px;
  border-bottom: 1px solid #eef2f6;
}

.certificate-summary dt {
  color: #8a98a5;
  font: normal normal 600 11px/16px Poppins;
  text-transform: uppercase;
}

.certificate-summary dd {
  margin: 0;
  color: #101820;
  font: normal normal 600 14px/22px Poppins;
  word-break: break-word;
}

.certificate-summary__actions {
  display: grid;
  gap: 10px;
  margin-top: 20px;
}

.certificate-action {
  min-height: 42px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px 14px;
  border: 1px solid #dce7f2;
  border-radius: 999px;
  color: #0076ff;
  background: #fff;
  text-decoration: none;
  font: normal normal 700 13px/20px Poppins;
}

.certificate-action--primary {
  background: #0076ff;
  color: #fff;
  border-color: #0076ff;
}

.certificate-action--download {
  display: none;
}

.certificate-action--disabled {
  color: #8a98a5;
  background: #f5f7f9;
  border-color: #e3e8ed;
  cursor: not-allowed;
  opacity: 0.75;
}

.certificate-action .v-icon {
  color: currentColor;
}

.certificate-preview {
  grid-column: 1;
  grid-row: 1;
  min-height: 680px;
  overflow: hidden;
}

.certificate-preview__header {
  min-height: 58px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 16px 18px;
  border-bottom: 1px solid #eef2f6;
}

.certificate-preview__header span {
  color: #6b7785;
  font: normal normal 700 12px/18px Poppins;
}

.certificate-preview__header-actions {
  display: flex;
  align-items: center;
  gap: 6px;
}

.certificate-preview iframe,
.certificate-preview img {
  width: 100%;
  height: 720px;
  display: block;
  border: 0;
  background: #fff;
}

.certificate-preview img {
  height: auto;
  max-height: 720px;
  object-fit: contain;
}

.certificate-empty {
  display: grid;
  grid-column: 1 / -1;
  min-height: 680px;
  justify-items: center;
  align-content: center;
  gap: 10px;
  padding: 34px 22px;
  text-align: center;
}

.certificate-empty .v-icon {
  color: #8a98a5;
}

.certificate-empty p {
  margin: 0;
  color: #6b7785;
  font: normal normal 400 14px/22px Poppins;
}

@media (max-width: 900px) {
  .certificate-layout {
    grid-template-columns: 1fr;
  }

  .certificate-preview {
    grid-column: auto;
    grid-row: auto;
  }

  .certificate-summary {
    display: none;
  }

  .certificate-summary--open {
    position: fixed;
    inset: 0;
    z-index: 1000;
    display: block;
    width: 100%;
    height: 100vh;
    height: 100dvh;
    overflow-y: auto;
    padding: 24px;
    border: 0;
    border-radius: 0;
  }

  .certificate-summary__close,
  .certificate-details-trigger {
    display: inline-flex;
  }

  .certificate-preview {
    min-height: auto;
  }

  .certificate-empty {
    min-height: 520px;
  }

  .certificate-preview iframe,
  .certificate-preview img {
    height: 70vh;
  }
}

@media (max-width: 560px) {
  .certificate-hero {
    padding: 22px 16px 26px;
  }

  .certificate-layout {
    padding: 16px;
  }

  .certificate-summary {
    padding: 16px;
  }

  .certificate-preview__header {
    min-height: 52px;
    padding: 14px 16px;
  }

  .certificate-preview iframe,
  .certificate-preview img {
    height: 64vh;
  }

  .certificate-empty {
    min-height: 360px;
  }
}
</style>
